/**
 * Pure types and builders for the modular UpgradeLimitDialog.
 *
 * The dialog is driven entirely by an UpgradeLimitConfig object so that future
 * limits (domains, credits, sends, etc.) can reuse the same component without
 * forking the UI.
 */

import {
  classifyEmailSendQuota,
  describeEmailSendQuota,
  type EmailSendQuotaDetails,
  formatAllowanceResetDate,
  formatSendCount,
  remainingEmailSends,
} from '@/lib/billing/email-send-quota-copy'
import {
  BILLING_PLAN_ORDER,
  BILLING_PLANS,
  type BillingPlanKey,
  buildPlanFeatures,
  isSunsetPlanKey,
  type SelfServePlanKey,
} from '@/lib/billing/plans'
import { defaultPriceSummary } from '@/lib/billing/price-summary'
import { GROWTH_PROMO } from '@/lib/billing/promo'
import { BREW_DEMO_BOOKING_URL } from '@/lib/site/booking'

export type UpgradeLimitType = 'brands' | 'domains' | 'credits' | 'emailSends'

export type UpgradeLimitCta = {
  label: string
  /**
   * When set, clicking the CTA triggers Stripe checkout for this plan.
   * When unset, the CTA navigates to /plan.
   */
  planKey?: SelfServePlanKey
  /**
   * When set, clicking the CTA navigates to this URL (overrides checkout).
   * Used for the Enterprise contact link.
   */
  href?: string
}

export type UpgradeLimitConfig = {
  limitType: UpgradeLimitType
  title: string
  description: string
  /**
   * Optional usage chip rendered next to the title (e.g. "3 / 1 brand
   * workspaces"). `display` replaces the `used / limit unit` reading when
   * the two numbers are not a used-of-limit pair (a shortfall).
   */
  currentUsage?: { used: number; limit: number; unit: string; display?: string }
  /** Plan key the dialog is recommending. Used for the highlighted CTA. */
  recommendedPlanKey?: BillingPlanKey
  /** Bulleted highlights of the recommended plan. */
  highlights?: ReadonlyArray<string>
  primaryCta: UpgradeLimitCta
  secondaryCta?: UpgradeLimitCta
}

export const ENTERPRISE_CONTACT_URL = BREW_DEMO_BOOKING_URL

type PlanLimitField = 'credits' | 'emailSends' | 'domains' | 'brands'

const LIMIT_TYPE_TO_PLAN_FIELD: Record<UpgradeLimitType, PlanLimitField> = {
  brands: 'brands',
  domains: 'domains',
  credits: 'credits',
  emailSends: 'emailSends',
}

/**
 * Returns the next plan tier that actually RAISES the limit the user just hit,
 * or null when no higher self-serve tier does (→ the dialog falls back to a
 * "Talk to sales" CTA).
 *
 * Limit-aware on purpose: a candidate tier only qualifies if it strictly
 * RAISES the metric the user hit. That keeps every dialog correct even if two
 * adjacent plans ever share a value on one metric (e.g. equal brand counts) —
 * the walk simply skips to the next tier that actually increases it, with no
 * per-limit special cases.
 *
 * Sunset tiers are never RECOMMENDED (they can't be bought), but they stay in
 * `BILLING_PLAN_ORDER` so an org already on one still walks forward correctly
 * from it.
 */
export function getNextPlanForLimit(
  currentPlanKey: BillingPlanKey,
  limitType: UpgradeLimitType
): BillingPlanKey | null {
  const field = LIMIT_TYPE_TO_PLAN_FIELD[limitType]
  const currentIndex = BILLING_PLAN_ORDER.indexOf(currentPlanKey)
  const currentValue = BILLING_PLANS[currentPlanKey][field]
  const startIndex = currentIndex === -1 ? 0 : currentIndex + 1

  for (let i = startIndex; i < BILLING_PLAN_ORDER.length; i++) {
    const candidateKey = BILLING_PLAN_ORDER[i]
    if (isSunsetPlanKey(candidateKey)) {
      continue
    }
    if (BILLING_PLANS[candidateKey][field] > currentValue) {
      return candidateKey
    }
  }

  return null
}

function formatBrandCount(count: number): string {
  if (count === 1) {
    return '1 brand workspace'
  }
  if (count >= Number.MAX_SAFE_INTEGER / 2) {
    return 'unlimited brand workspaces'
  }
  return `${count} brand workspaces`
}

export function buildBrandLimitConfig(input: {
  currentPlanKey: BillingPlanKey
  used: number
  limit: number
}): UpgradeLimitConfig {
  const currentPlan = BILLING_PLANS[input.currentPlanKey]
  const recommendedPlanKey = getNextPlanForLimit(input.currentPlanKey, 'brands')
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null

  const limitLabel = formatBrandCount(input.limit)

  const description = recommendedPlan
    ? `You've reached the ${limitLabel} limit on the ${currentPlan.name} plan. Delete or customize an existing brand to free a slot, or upgrade to ${recommendedPlan.name} for ${formatBrandCount(recommendedPlan.brands)}.`
    : `You've reached the ${limitLabel} limit on the ${currentPlan.name} plan. Delete or customize an existing brand to free a slot, or contact sales to add more brand workspaces.`

  const highlights = recommendedPlan
    ? buildPlanHighlights(recommendedPlan.key)
    : []

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  return {
    limitType: 'brands',
    title: 'Upgrade to add another brand',
    description,
    currentUsage: {
      used: input.used,
      limit: input.limit,
      unit: 'brand workspaces',
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    highlights,
    primaryCta,
    // The brands dialog points at brand management (not /plan): the user can
    // delete or customize an existing brand to free a slot as an alternative
    // to upgrading. The other limit dialogs keep "View all plans" → /plan.
    secondaryCta: {
      label: 'Manage brands',
      href: '/brand',
    },
  }
}

function formatCreditsCount(count: number): string {
  if (count <= 0) {
    return 'no AI credits'
  }
  return `${count.toLocaleString()} AI credits`
}

/**
 * Build the upgrade-dialog config shown when an org has used all of its AI
 * credits for the current billing period. Mirrors `buildBrandLimitConfig`
 * so every limit dialog is data-driven from `BILLING_PLANS` — no hardcoded
 * credit numbers or plan names in UI files.
 */
export function buildCreditsLimitConfig(input: {
  currentPlanKey: BillingPlanKey
  used: number
  limit: number
}): UpgradeLimitConfig {
  const currentPlan = BILLING_PLANS[input.currentPlanKey]
  const recommendedPlanKey = getNextPlanForLimit(
    input.currentPlanKey,
    'credits'
  )
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null

  const description = recommendedPlan
    ? `You've used all ${formatCreditsCount(input.limit)} on the ${currentPlan.name} plan. Upgrade to ${recommendedPlan.name} for ${formatCreditsCount(recommendedPlan.credits)} each month.`
    : `You've used all ${formatCreditsCount(input.limit)} on the ${currentPlan.name} plan. Contact sales for a custom AI credit allotment.`

  const highlights = recommendedPlan
    ? buildPlanHighlights(recommendedPlan.key)
    : []

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  return {
    limitType: 'credits',
    title: 'Upgrade to keep generating with AI',
    description,
    currentUsage: {
      used: input.used,
      limit: input.limit,
      unit: 'AI credits',
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    highlights,
    primaryCta,
    secondaryCta: {
      label: 'View all plans',
      href: '/plan',
    },
  }
}

/**
 * Build the upgrade-dialog config shown when a contact-validation run costs
 * more credits than the org has left. Unlike `buildCreditsLimitConfig` (a
 * fully-drained balance), this is a SHORTFALL dialog: the usage chip shows
 * `remaining / required credits` so the user sees exactly how far short they
 * are of the quoted validation cost.
 */
export function buildValidationCreditsConfig(input: {
  currentPlanKey: BillingPlanKey
  requiredCredits: number
  remainingCredits: number
}): UpgradeLimitConfig {
  const recommendedPlanKey = getNextPlanForLimit(
    input.currentPlanKey,
    'credits'
  )
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null

  const required = Math.max(0, Math.round(input.requiredCredits))
  const remaining = Math.max(0, Math.floor(input.remainingCredits))

  const description = recommendedPlan
    ? `Validating these contacts costs ${required.toLocaleString()} credits, but you have ${remaining.toLocaleString()} left this period. Upgrade to ${recommendedPlan.name} for ${formatCreditsCount(recommendedPlan.credits)} each month.`
    : `Validating these contacts costs ${required.toLocaleString()} credits, but you have ${remaining.toLocaleString()} left this period. Contact sales for a custom AI credit allotment.`

  const highlights = recommendedPlan
    ? buildPlanHighlights(recommendedPlan.key)
    : []

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  return {
    limitType: 'credits',
    title: 'Upgrade to validate your contacts',
    description,
    currentUsage: {
      used: remaining,
      limit: required,
      unit: 'credits',
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    highlights,
    primaryCta,
    secondaryCta: {
      label: 'View all plans',
      href: '/plan',
    },
  }
}

function formatDomainsCount(count: number): string {
  if (count === 1) {
    return '1 sending domain'
  }
  if (count >= Number.MAX_SAFE_INTEGER / 2) {
    return 'unlimited sending domains'
  }
  return `${count} sending domains`
}

/**
 * Build the upgrade-dialog config for the sending-domain limit. Sending
 * domains are UNLIMITED on every plan (2026-08-19), so the add-domain gate in
 * `domain-settings.tsx` can no longer fire and this config is a defensive
 * fallback — it must still read sanely if a future plan reintroduces a cap.
 */
export function buildDomainsLimitConfig(input: {
  currentPlanKey: BillingPlanKey
  used: number
  limit: number
}): UpgradeLimitConfig {
  const currentPlan = BILLING_PLANS[input.currentPlanKey]
  const recommendedPlanKey = getNextPlanForLimit(
    input.currentPlanKey,
    'domains'
  )
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null

  const limitLabel = formatDomainsCount(input.limit)

  const buildFallbackDescription = () => {
    if (input.limit >= Number.MAX_SAFE_INTEGER) {
      return 'Sending domains are unlimited on every plan. If adding one is blocked, contact support.'
    }
    return `You've reached the ${limitLabel} limit on the ${currentPlan.name} plan. Contact sales to add more sending domains.`
  }
  const description = recommendedPlan
    ? `You've reached the ${limitLabel} limit on the ${currentPlan.name} plan. Upgrade to ${recommendedPlan.name} for ${formatDomainsCount(recommendedPlan.domains)}.`
    : buildFallbackDescription()

  const highlights = recommendedPlan
    ? buildPlanHighlights(recommendedPlan.key)
    : []

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  return {
    limitType: 'domains',
    title: 'Upgrade to add another sending domain',
    description,
    currentUsage: {
      used: input.used,
      limit: input.limit,
      unit: 'sending domains',
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    highlights,
    primaryCta,
    secondaryCta: {
      label: 'View all plans',
      href: '/plan',
    },
  }
}

function formatEmailSendsCount(count: number): string {
  if (count <= 0) {
    return 'unlimited email sends'
  }
  return `${count.toLocaleString()} email sends`
}

/**
 * The next tier whose monthly sends exceed the org's CURRENT period limit,
 * not just its plan's catalog value: a prepaid term (6 × Growth = 300,000)
 * must never be "upgraded" to Pro's 200,000.
 */
function getNextPlanForSends(
  currentPlanKey: BillingPlanKey,
  periodLimit: number
): BillingPlanKey | null {
  let candidate = getNextPlanForLimit(currentPlanKey, 'emailSends')
  while (candidate && BILLING_PLANS[candidate].emailSends <= periodLimit) {
    candidate = getNextPlanForLimit(candidate, 'emailSends')
  }
  return candidate
}

/** What the email-send dialog renders: the plan, its numbers, the refusal. */
export type EmailSendsLimitInput = {
  currentPlanKey: BillingPlanKey
} & Pick<EmailSendQuotaDetails, 'used' | 'limit'> &
  Partial<Pick<EmailSendQuotaDetails, 'reserved' | 'requested' | 'periodEnd'>>

/**
 * Build the email-send upgrade dialog. Admission is `limit − used −
 * reserved`, so a refused send has three honest stories (see
 * `classifyEmailSendQuota`): the allowance is used up, this send is bigger
 * than what is left, or sends in progress reserved the rest. Only the first
 * says "used all". The sentences are the shared ones
 * (`describeEmailSendQuota`); plan numbers come straight from `BILLING_PLANS`.
 */
export function buildEmailSendsLimitConfig(
  input: EmailSendsLimitInput
): UpgradeLimitConfig {
  const currentPlan = BILLING_PLANS[input.currentPlanKey]
  const recommendedPlanKey = getNextPlanForSends(
    input.currentPlanKey,
    input.limit
  )
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null
  const quota = {
    used: input.used,
    reserved: input.reserved ?? 0,
    limit: input.limit,
    requested: input.requested,
  }
  const remaining = remainingEmailSends(quota)
  const kind = classifyEmailSendQuota(quota)
  const upgradeSentence = recommendedPlan
    ? `Upgrade to ${recommendedPlan.name} for ${formatEmailSendsCount(recommendedPlan.emailSends)} each month.`
    : 'Contact sales for a custom monthly send volume.'
  let fewerSentence = ''
  if (kind === 'shortfall') {
    fewerSentence =
      quota.reserved > 0
        ? ' Or send to fewer recipients, or cancel a send in progress.'
        : ' Or send to fewer recipients.'
  }
  const resetSentence =
    input.periodEnd === undefined
      ? ''
      : ` Your allowance resets on ${formatAllowanceResetDate(input.periodEnd)}.`
  const description = `${describeEmailSendQuota({ ...quota, planName: currentPlan.name })} ${upgradeSentence}${fewerSentence}${resetSentence}`

  const highlights = recommendedPlan
    ? buildPlanHighlights(recommendedPlan.key)
    : []
  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)
  const secondaryCta = { label: 'View all plans', href: '/plan' }

  if (kind === 'shortfall' || kind === 'reserved_only') {
    const isShortfall = kind === 'shortfall'
    return {
      limitType: 'emailSends',
      title: isShortfall
        ? 'Not enough email sends left for this send'
        : 'Your remaining email sends are reserved',
      description,
      currentUsage: isShortfall
        ? {
            used: remaining,
            limit: quota.requested ?? 0,
            unit: 'email sends',
            display: `${formatSendCount(remaining)} left · ${formatSendCount(quota.requested ?? 0)} needed`,
          }
        : {
            used: quota.used + quota.reserved,
            limit: quota.limit,
            unit: 'email sends',
            display: `${formatSendCount(quota.reserved)} reserved · ${formatSendCount(quota.used)} used of ${formatSendCount(quota.limit)}`,
          },
      recommendedPlanKey: recommendedPlanKey ?? undefined,
      highlights,
      primaryCta,
      secondaryCta,
    }
  }

  return {
    limitType: 'emailSends',
    title: 'Upgrade to keep sending emails',
    description,
    currentUsage: {
      used: input.used,
      limit: input.limit,
      unit: 'email sends',
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    highlights,
    primaryCta,
    secondaryCta,
  }
}

export function showEmailSendsUpgrade(
  showUpgrade: (config: UpgradeLimitConfig) => void,
  input: EmailSendsLimitInput
): void {
  showUpgrade(buildEmailSendsLimitConfig(input))
}

function buildPrimaryCta(
  recommendedPlanKey: BillingPlanKey | null,
  recommendedPlanName: string | undefined
): UpgradeLimitCta {
  if (!recommendedPlanKey) {
    return {
      label: 'Talk to sales',
      href: ENTERPRISE_CONTACT_URL,
    }
  }

  if (recommendedPlanKey === 'enterprise') {
    return {
      label: 'Talk to sales',
      href: ENTERPRISE_CONTACT_URL,
    }
  }

  return {
    label: `Upgrade to ${recommendedPlanName ?? 'next plan'}`,
    planKey: recommendedPlanKey as SelfServePlanKey,
  }
}

function buildPlanHighlights(planKey: BillingPlanKey): ReadonlyArray<string> {
  const features = buildPlanFeatures(planKey)
  // Pull the first 3 non-"Everything in" feature lines as concise highlights.
  const highlights: Array<string> = []
  for (const feature of features) {
    if (feature.name.toLowerCase().startsWith('everything in')) {
      continue
    }
    highlights.push(feature.name)
    if (highlights.length >= 3) {
      break
    }
  }
  return highlights
}

/**
 * At a usage limit the primary CTA stays month-to-month — that unblocks the
 * action with the lowest up-front charge. Eligible never-paid orgs still need
 * a path to the prepaid offer, which is the Starter replacement. Rewrite only
 * the "View all plans" recovery link; brand-management and sales CTAs stay.
 */
export function withPromoLimitSecondaryCta(
  config: UpgradeLimitConfig,
  isEligible: boolean
): UpgradeLimitConfig {
  if (!isEligible) {
    return config
  }
  if (config.recommendedPlanKey !== GROWTH_PROMO.planKey) {
    return config
  }
  if (config.secondaryCta?.href !== '/plan') {
    return config
  }

  const price = defaultPriceSummary()[GROWTH_PROMO.planKey]
  if (!price) {
    return config
  }

  return {
    ...config,
    secondaryCta: {
      href: '/plan',
      // Keep the navigational meaning ("this compares plans") — a user at a
      // hard limit still needs a labelled way to weigh options, not only an
      // offer string.
      label: `View all plans — ${GROWTH_PROMO.percentOff}% off for ${GROWTH_PROMO.termMonths} months`,
    },
  }
}
